import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { CheckCircle2, XCircle, Clock, MessageSquare, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const StatusPill = ({ s }) => {
  const map = {
    pending:  { label: 'Pending', cls: 'bg-amber-50 text-amber-800 border-amber-200', icon: Clock },
    approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
    rejected: { label: 'Rejected', cls: 'bg-rose-50 text-rose-800 border-rose-200', icon: XCircle },
  };
  const info = map[s] || map.pending;
  const Icon = info.icon;
  return (
    <Badge className={`${info.cls} border font-medium gap-1`}>
      <Icon className="w-3 h-3" />
      {info.label}
    </Badge>
  );
};

export const TopicRequestsAdmin = () => {
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ class_range: '', narration: '', formulas: '', background: 'chalkboard' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/storyteller/requests?status=${status}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems(await res.json());
    } catch (e) {
      toast.error('Failed to load topic requests');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const openApprove = (r) => {
    setSelected(r);
    setForm({ class_range: '', narration: '', formulas: '', background: 'chalkboard' });
  };

  const submitApprove = async () => {
    if (!selected) return;
    const narration = form.narration.trim();
    if (narration.length < 60) {
      toast.error('Narration must be at least 60 characters (aim for 150-300)');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/storyteller/requests/${selected.request_id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          narration,
          class_range: form.class_range.trim(),
          background: form.background || 'chalkboard',
          formulas: form.formulas.split('\n').map(x => x.trim()).filter(Boolean),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success('Topic approved and published');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Approve failed');
    } finally {
      setSaving(false);
    }
  };

  const reject = async (r) => {
    if (!confirm(`Reject request "${r.title}"?`)) return;
    try {
      const res = await fetch(`${API}/api/storyteller/requests/${r.request_id}/reject`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Request rejected');
      load();
    } catch (e) {
      toast.error('Reject failed');
    }
  };

  const StatusBadge = null; void StatusBadge;

  return (
    <div className="p-6 space-y-4" data-testid="topic-requests-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-primary" />
            Concept Whiteboard — Topic Requests
          </h1>
          <p className="text-sm text-muted-foreground">Students can request new struggle-topics from the Concept Whiteboard.  Approve to add them to the seed bank.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-requests-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="flex gap-2">
        {['pending', 'approved', 'rejected'].map(s => (
          <Button
            key={s}
            variant={status === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatus(s)}
            data-testid={`filter-${s}`}
            className="capitalize"
          >
            {s}
          </Button>
        ))}
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="empty-state">
            No {status} topic requests
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">Subject</th>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Note</th>
                <th className="text-left px-4 py-2">Requested by</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.request_id} className="border-t hover:bg-muted/30" data-testid={`request-row-${r.request_id}`}>
                  <td className="px-4 py-3 font-medium">{r.subject}</td>
                  <td className="px-4 py-3">{r.title}</td>
                  <td className="px-4 py-3 text-muted-foreground max-w-xs truncate" title={r.note}>{r.note || '—'}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{r.requested_by || '—'}</td>
                  <td className="px-4 py-3"><StatusPill s={r.status} /></td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {r.status === 'pending' ? (
                      <>
                        <Button size="sm" onClick={() => openApprove(r)} data-testid={`approve-${r.request_id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(r)} data-testid={`reject-${r.request_id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {r.approved_topic_id ? `→ ${r.approved_topic_id}` : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Approve modal */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Approve topic request</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-2 items-center">
                <Badge variant="secondary">{selected.subject}</Badge>
                <span className="font-semibold">{selected.title}</span>
              </div>
              {selected.note && (
                <div className="p-3 rounded bg-muted/30 text-xs text-muted-foreground italic">
                  &ldquo;{selected.note}&rdquo;
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold mb-1">Class range (e.g. 8-10)</label>
                <Input
                  value={form.class_range}
                  onChange={e => setForm({ ...form, class_range: e.target.value })}
                  placeholder="8-10"
                  data-testid="approve-class-range"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">
                  Narration <span className="text-muted-foreground">(60-500 words is best)</span>
                </label>
                <Textarea
                  rows={8}
                  value={form.narration}
                  onChange={e => setForm({ ...form, narration: e.target.value })}
                  placeholder="Write the narrator's explanation — plain conversational tone, targeted at the class range."
                  data-testid="approve-narration"
                />
                <div className="text-[10px] text-muted-foreground mt-1">
                  {form.narration.length} chars {form.narration.length < 60 && '· need at least 60'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">
                  Formulas (one per line — optional)
                </label>
                <Textarea
                  rows={2}
                  value={form.formulas}
                  onChange={e => setForm({ ...form, formulas: e.target.value })}
                  placeholder="F = m · a"
                  data-testid="approve-formulas"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Background hint</label>
                <select
                  className="w-full rounded border bg-background px-3 py-2 text-sm"
                  value={form.background}
                  onChange={e => setForm({ ...form, background: e.target.value })}
                  data-testid="approve-background"
                >
                  <option value="chalkboard">chalkboard</option>
                  <option value="graph">graph</option>
                  <option value="lab">lab</option>
                  <option value="molecule">molecule</option>
                  <option value="leaves">leaves</option>
                  <option value="clouds">clouds</option>
                  <option value="space">space</option>
                  <option value="map">map</option>
                  <option value="book">book</option>
                  <option value="circuit">circuit</option>
                  <option value="triangle">triangle</option>
                  <option value="grid">grid</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
            <Button onClick={submitApprove} disabled={saving} data-testid="approve-submit">
              {saving ? 'Publishing…' : 'Approve & Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TopicRequestsAdmin;
